import Input from "components/Input";
import { useAppContext } from "contexts";
import Select from "react-select";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import apiService from "services/apiService";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getFormatDate } from "util/epochTime";
import { sortByKey } from "util/sortArrByName";
import HeaderButtons from "components/Button/HeaderButtons";
import { customSelectStyles } from "util/selectStyles";
import { validateNoTrailingOrLeadingWhitespace } from "util/validation";

const schema = yup.object().shape({
  employeeEmail: yup.string().required("Employee Name is required"),
  review: validateNoTrailingOrLeadingWhitespace("Review")
    .max(1250, "Maximum 1250 characters allowed")
    .required(),
  reviewer: yup.string().required("Reviewer is required"),
  reviewDate: yup.string().required("Review Date is required"),
});

type AddReviewFormData = yup.InferType<typeof schema>;

interface AddProps {
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

interface OptionProps {
  value: string;
  label: string;
}

type UserOptionSource = { email?: string; displayName?: string };

const AddReview = ({ onClose, onSuccess, onError }: AddProps) => {
  const [userdata, setUserData] = React.useState<OptionProps[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userPage, setUserPage] = React.useState(0);
  const [userSearch, setUserSearch] = React.useState("");
  const [hasMoreUsers, setHasMoreUsers] = React.useState(true);
  const [loadingUsers, setLoadingUsers] = React.useState(false);
  const USER_PAGE_SIZE = 10;

  const getUsersFromPayload = (payload: unknown): UserOptionSource[] => {
    if (Array.isArray(payload)) {
      return payload as UserOptionSource[];
    }

    if (
      typeof payload === "object" &&
      payload !== null &&
      "content" in payload &&
      Array.isArray((payload as { content?: unknown }).content)
    ) {
      return (payload as { content: UserOptionSource[] }).content;
    }

    return [];
  };

  const { user } = useAppContext();
  const dateStore = Date.now();
  const formattedDate = getFormatDate(new Date(dateStore));

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    // mode: "onBlur",
    defaultValues: {
      reviewer: user?.displayName || "",
      reviewDate: formattedDate,
      employeeEmail: "",
      review: "",
    },
  });

  const watchEmployeeEmail = methods.watch("employeeEmail");
  const watchReview = methods.watch("review");

  const { formState: { errors,  isDirty } } = methods;

  const fetchUsers = async (page: number, searchValue: string, append: boolean) => {
    if (loadingUsers) return;

    setLoadingUsers(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(page));
      query.set("size", String(USER_PAGE_SIZE));
      if (searchValue.trim()) {
        query.set("search", searchValue.trim());
      }

      const res = await apiService.get(`api/users?${query.toString()}`);
      const payload = res.data;
      const users = getUsersFromPayload(payload);

      const options = users.map((item) => ({
        value: item.email,
        label: item.displayName,
      }));

      setUserData((prev) => {
        const merged = append ? [...prev, ...options] : options;
        const dedup = new Map<string, OptionProps>();
        merged.forEach((item) => {
          if (!dedup.has(item.value)) {
            dedup.set(item.value, item);
          }
        });
        return sortByKey(Array.from(dedup.values()), "label");
      });

      setUserPage(page);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(page < payload.totalPages - 1);
      } else {
        setHasMoreUsers(options.length === USER_PAGE_SIZE);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchUsers(0, userSearch, false);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [userSearch]);

  const submit = async (data: AddReviewFormData) => {
    setIsSubmitting(true);
    const newData = { ...data, id: 0, reviewDate: dateStore };
    try {
      const res = await apiService.post("api/review", newData);
      if (res.status === 200) {
        onSuccess?.("Review added successfully");
      } else {
        onError?.(res.statusText || "Something went wrong");
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Something went wrong";
      onError?.(message);
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submit)} className="space-y-4 pt-1">

        {/* Employee Select */}
        <div>
          <label htmlFor="employeeEmailSelect" className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white mb-1.5">
            Employee Name <span className="text-red-500">*</span>
          </label>
          <Select
            {...methods.register("employeeEmail", { required: true })}
            inputId="employeeEmailSelect"
            options={userdata}
            placeholder="Select Employee"
            className="w-full mt-2"
            isLoading={loadingUsers}
            onMenuScrollToBottom={() => {
              if (hasMoreUsers && !loadingUsers) {
                void fetchUsers(userPage + 1, userSearch, true);
              }
            }}
            onInputChange={(value, actionMeta) => {
              if (actionMeta.action === "input-change") {
                setUserSearch(value);
              }
              if (actionMeta.action === "menu-close") {
                setUserSearch("");
              }
              return value;
            }}
            styles={customSelectStyles}

            onChange={(selectedOption) => {
              selectedOption &&
                methods.setValue("employeeEmail", selectedOption.value);
            }}
          />
          {errors.employeeEmail && (
            <p className="text-xs text-red-500 mt-1">{errors.employeeEmail.message}</p>
          )}
        </div>

        {/* Review */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white mb-1.5">
            Review <span className="text-red-500">*</span>
          </label>

          {/* <textarea
            name="review"
            className="shared-input-field bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 
            dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 
            disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 
            dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
            placeholder="Write the review (max 1250 characters)"
          // compact
          ></textarea> */}
          <Input
            name="review"
            label=""
            placeholder="Write the review (max 1250 characters)"
            type="textarea"
          />

        </div>

        {/* Reviewer + Date — two columns */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white mb-1.5">
              {/* <UserCheck size={14} className="text-blue-500" /> */}
              Reviewer
            </label>
            <Input name="reviewer" label="" placeholder="Reviewer" disabled compact />
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white mb-1.5">
              Review Date
            </label>
            <Input name="reviewDate" label="" placeholder="Review Date" disabled compact />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <HeaderButtons
            type="button"
            variant="tertiary"
            size="medium"
            onClick={onClose}
          >
            Cancel
          </HeaderButtons>
          <HeaderButtons
            type="submit"
            variant="primary"
            size="medium"
            loading={isSubmitting}
            disabled={isSubmitting ||
              !isDirty ||
              // !isValid ||
              !watchEmployeeEmail ||
              !watchReview.trim()}
          >
            {isSubmitting ? "Submitting…" : "Submit Review"}
          </HeaderButtons>
        </div>

      </form>
    </FormProvider>
  );
};

export default AddReview;
